import React, { useEffect, useRef } from "react";

import { Icon } from "Components/atoms/Icon";
import { useCalendarContext } from "shared/contexts/CalendarContext";
import { useGeneralContext } from "shared/contexts/GeneralContext";
import { useSearchContext } from "shared/contexts/SearchContext";
import { formatDateLocalized } from "shared/utils/formatDateLocalized";
import { getFormattedDateDetails } from "shared/utils/getFormattedDateDetails";
import { DatePicker } from "../../DatePicker/Desktop";
import { DatePickerWithInputProps } from "../types";
import {
  HoverIcon,
  InputContainer,
  Placeholder,
  SelectedDate,
  Wrapper,
} from "./styles";

export const DatePickerWithInput: React.FC<DatePickerWithInputProps> = ({
  placeholder,
  maxSelectableDates,
  daysInMonth,
  type = "origin",
  selectedDates = [],
  onDatesChange = () => {},
  isDisabled = false,
}) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const { isOpenOriginCalendar } = useGeneralContext();
  const {
    activeDesktopCalendar,
    selectionMode,
    selectedOriginDates,
    selectedDestinationDates,
    setActiveDesktopCalendar,
    setSelectedOriginDates,
    setSelectedDestinationDates,
  } = useCalendarContext();
  const { setDepartureDates, setReturnDates } = useSearchContext();

  const activeType = activeDesktopCalendar || "origin";
  const activeDates =
    activeType === "origin" ? selectedOriginDates : selectedDestinationDates;
  const rendersSharedCalendar =
    type === "origin" && activeDesktopCalendar !== null;

  useEffect(() => {
    if (type === "origin" && isOpenOriginCalendar) {
      setActiveDesktopCalendar("origin");
    }
  }, [isOpenOriginCalendar, setActiveDesktopCalendar, type]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (target.closest("[data-date-field]")) return;
      if (activeDesktopCalendar && !wrapperRef.current?.contains(target)) {
        setActiveDesktopCalendar(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [activeDesktopCalendar, setActiveDesktopCalendar]);

  const updateActiveDates = (dates: string[]) => {
    if (activeType === "origin") {
      setSelectedOriginDates(dates);
      setDepartureDates(dates);
    } else {
      setSelectedDestinationDates(dates);
      setReturnDates(dates);
    }
  };

  const closeSharedCalendar = () => {
    setActiveDesktopCalendar(
      activeType === "origin" && selectionMode === "multi"
        ? "destination"
        : null,
    );
  };

  const handleRemoveDate = (dateToRemove: string) => {
    onDatesChange(selectedDates.filter((date) => date !== dateToRemove));
  };

  return (
    <Wrapper ref={wrapperRef} data-date-field={type}>
      <InputContainer
        onClick={() =>
          !isDisabled ? setActiveDesktopCalendar(type) : undefined
        }
        aria-label={`Abrir seletor de datas para ${type === "origin" ? "ida" : "volta"}`}
      >
        {selectedDates.length > 0 ? (
          selectedDates.map((date) => (
            <SelectedDate
              type={type}
              key={date}
              onClick={(event) => {
                event.stopPropagation();
                handleRemoveDate(date);
              }}
              aria-label={`Remover data ${formatDateLocalized(date)}`}
            >
              <span>{getFormattedDateDetails(date).day}</span>
              <span>{getFormattedDateDetails(date).monthShort}</span>
              <Icon
                library="go"
                name={
                  type === "destination" ? "GoArrowDownLeft" : "GoArrowUpRight"
                }
                size="extraSmall"
                color="black"
              />
              <HoverIcon>
                <Icon
                  library="md"
                  name="MdOutlineClose"
                  size="small"
                  color="black"
                />
              </HoverIcon>
            </SelectedDate>
          ))
        ) : (
          <Placeholder>{placeholder}</Placeholder>
        )}
      </InputContainer>

      {rendersSharedCalendar && (
        <DatePicker
          closePicker={closeSharedCalendar}
          maxSelectableDates={maxSelectableDates}
          onDatesChange={updateActiveDates}
          selectedDates={activeDates}
          daysInMonth={daysInMonth}
          onConfirm={(dates) => {
            updateActiveDates(dates);
            closeSharedCalendar();
          }}
          onClear={() => updateActiveDates([])}
          type={activeType}
        />
      )}
    </Wrapper>
  );
};
